/**
 * Component to configure a donation with a gift certifictae for one ore more people
 */
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {TranslateService} from "@ngx-translate/core";
import {Donation, Gift} from "../donation/donation";
import {OptionService} from "../option/option.service";

declare const __twingle_giftUrl: string;

@Component({
  selector: 'gift-donation',
  templateUrl: './gift.donation.html',
  providers: []
})
export class GiftDonationComponent implements OnInit, OnChanges {

  /**
   * formgroup for validation of the input
   */
  @Input() form: FormGroup;

  /**
   * Holds the donation where the gift data is stored in.
   */
  @Input() donation: Donation;

  /**
   * eventemitter if the form changes
   */
  @Output() formChanged: EventEmitter<boolean> = new EventEmitter();

  /**
   * Eventemitter if the number of recipient changes
   */
  @Output() receiverCountChanged: EventEmitter<number> = new EventEmitter();

  /**
   * list of products the user should choose
   */
  @Input() products: [any, string][] = [];

  /**
   * the selected product
   */
  @Input() product: any = null;

  /**
   * name for the overlay in case the second receiver is added
   */
  @Input() overlayName: string = null;

  /**
   * any value that can be used to trigger updates on the preview
   */
  @Input() updateValueForPreview: any = null;

  /**
   * maximal number of receivers the suer can add
   */
  receiverMax = 5;

  /**
   * list of salutation for the form
   */
  salutations: any;

  /**
   * toggle for showing/hiding the form
   */
  showForm = false;

  /**
   * Holds the data for the gift document to send it to the backend.
   */
  giftData: Gift;

  /**
   * Icon for the button text
   */
  icon: string = null;

  /**
   * URL of the example for a gift donation.
   */
  previewImage: string;

  /**
   * toggle if the preview image is loading or not.
   */
  previewImageLoad: boolean;

  /**
   * Toggle for showing/hiding the overlay
   */
  showOverlay = false;

  /**
   * remember if we once had shown the overlay.
   * Is deactivated (true) on init because it should be only visible if a name is given.
   */
  overlayShown = true;

  /**
   * toggle if the gift donation is mandatory
   */
  isMandatory = false;

  /**
   * constructor
   * @param translate
   * @param optionService
   */
  constructor(private translate: TranslateService, private optionService: OptionService) {
  }

  /**
   * @inheritDoc
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    // if the updateValue has been changed, update the preview URL
    if (changes.hasOwnProperty('updateValueForPreview') && changes.updateValueForPreview.firstChange === false) {
      this.updatePreviewUrl();
    }
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // check if the gift is mandary
    this.isMandatory = OptionService.getOptionStatic('gift_donation') === "mandatory";

    if (this.donation.gift) {
      // we have already some data
      this.giftData = this.donation.gift;
      this.showForm = true;
    }
    else {
      // init the model
      this.giftData = new Gift();
      this.showForm = this.isMandatory;
    }

    if (this.overlayName) {
      // activate the overlay if we have a name
      this.overlayShown = false;
    }

    // get the icon
    this.icon = this.optionService.getOption('gift_donation_icon') || 'gift';

    // add the form elements for the values in the form.
    this.form.addControl('showForm', new FormControl(this.showForm));
    this.form.addControl('gift.sender', new FormControl(null, [Validators.required]));
    this.form.addControl('gift.message', new FormControl(null, [Validators.required]));

    // only add product model if we have products
    if (this.products.length > 0) {
      if (!this.giftData.product && this.product === null) {
        // init with the first item in the list.
        this.product = this.products[0][0];
        this.giftData.product = this.product;
      } else if (!this.giftData.product) {
        this.giftData.product = this.product;
      }

      this.form.addControl('gift.product', new FormControl(this.giftData.product, [Validators.required]));
    }

    // subscribe for form changes.
    this.form.valueChanges.subscribe(() => this.onFormChange());

    // salutation for the receivers
    this.translate.get(['contact.salutation.label', 'contact.salution.female.label',
      'contact.salution.male.label', 'contact.salution.divers.label']).subscribe(data => {
      this.salutations = [
        ['', data['contact.salutation.label']],
        ['female', data['contact.salution.female.label']],
        ['male', data['contact.salution.male.label']],
        ['divers', data['contact.salution.divers.label']],
      ];
    });

    this.initReceiver();

    // init validators
    this.toggleForm(this.showForm);

  }

  /**
   * if the form changes, emit eventemitter
   */
  onFormChange(): void {
    this.formChanged.emit(this.form.valid);
  }

  /**
   * switch on/off the form.
   * @param newValue
   */
  toggleForm(newValue: boolean): void {
    this.showForm = newValue;

    if (this.showForm === true) {
      // add elments to form
      if (this.products.length > 0) {
        this.form.controls['gift.product'].enable({onlySelf: true});
      }
      for (let i = 0; i < this.giftData.receiver.length; i++) {
        this.form.controls['gift.receiver.name.' + i].enable({onlySelf: true});
        // this.form.controls['gift.receiver.salutation.' + i].enable({onlySelf: true});
      }
      this.form.controls['gift.sender'].enable({onlySelf: true});
      // the last update needs to be for the whole form to update the status there
      this.form.controls['gift.message'].enable({onlySelf: false});
      this.receiverCountChanged.emit(this.giftData.receiver.length);

      this.donation.gift = this.giftData;

      // update the preview image
      this.updatePreviewUrl();
    } else {
      // remove elements/validators from form
      if (this.products.length > 0) {
        this.form.controls['gift.product'].disable({onlySelf: true});
      }
      for (let i = 0; i < this.giftData.receiver.length; i++) {
        this.form.controls['gift.receiver.name.' + i].disable({onlySelf: true});
        // this.form.controls['gift.receiver.salutation.' + i].disable({onlySelf: true});
      }
      this.form.controls['gift.sender'].disable({onlySelf: true});
      // the last update needs to be for the whole form to update the status there
      this.form.controls['gift.message'].disable({onlySelf: false});
      this.receiverCountChanged.emit(0);

      this.donation.gift = null;
    }
  }

  /**
   * Updates the preview URL
   */
  updatePreviewUrl(): void {
    if (this.showForm === false) {
      // do nothing if the form is not visible
      return;
    }

    // put togehther the selected products from the donation
    const productsForPreview = this.products.map(product => {
      const productInDonation = this.donation.products.find(donationProduct => donationProduct.product === product[1]);
      return product[0] + '-' + productInDonation.count + '-' + productInDonation.price;
    });

    // get the users locale
    const currentLocale = this.translate.currentLang;

    let url = __twingle_giftUrl;
    url += '/' + currentLocale;
    url += '?i=' + this.giftData.product;
    url += '&a=' + this.donation.amount;
    url += '&p=' + productsForPreview.join(',');
    if (this.giftData.receiver && this.giftData.receiver.length > 0 && this.giftData.receiver[0].name.length > 0) {
      url += '&r=' + encodeURIComponent(this.giftData.receiver[0].name);
    }
    if (this.giftData.sender) {
      url += '&s=' + encodeURIComponent(this.giftData.sender);
    }
    if (this.giftData.message) {
      url += '&m=' + encodeURIComponent(this.giftData.message);
    }
    if (this.giftData.hideAmount === true) {
      url += '&ha=1';
    }
    if (this.giftData.receiver?.length > 0) {
      url += '&rc=' + this.giftData.receiver.length;
    }

    if (url !== this.previewImage) {
      this.previewImageLoad = true;
      this.previewImage = url;
    }
  }

  /**
   * overlay  with hint is closed
   * @param result
   */
  submitOverlay(result: boolean): void {
    this.showOverlay = false;
    this.overlayShown = true;
    this.addReceiver();
  }

  /**
   * Overlay with hint is caceled
   */
  cancelOverlay(): void {
    this.showOverlay = false;
  }

  /**
   * initialize the receivers with data that is (maybe) already there or with an empty receiver.
   */
  initReceiver(): void {
    if (this.giftData.receiver.length === 0) {
      // add one empty receiver
      this.addReceiver();
    } else {
      let i = 0;
      for (const receiver of this.giftData.receiver) {
        this.form.addControl('gift.receiver.name.' + i, new FormControl(receiver.name, Validators.required));
        i++;
      }
      this.receiverCountChanged.emit(this.giftData.receiver.length);
    }
  }

  /**
   * adds a new receiver to the list
   */
  addReceiver(): void {
    if (this.giftData.receiver.length === 1 && this.overlayShown === false) {
      this.showOverlay = true;
    } else {
      this.form.addControl('gift.receiver.name.' + this.giftData.receiver.length, new FormControl(null, Validators.required));
      // this.form.addControl('gift.receiver.salutation.' + this.receiver.length, new FormControl());
      this.giftData.receiver.push({name: ''});
      this.receiverCountChanged.emit(this.giftData.receiver.length);
    }
  }

  /**
   * removes an existing receiver
   * @param index
   */
  removeReceiver(index: number): void {
    this.giftData.receiver.splice(index, 1);
    this.form.removeControl('gift.receiver.name.' + this.giftData.receiver.length);
    // this.form.removeControl('gift.receiver.salutation.' + this.receiver.length);
    this.receiverCountChanged.emit(this.giftData.receiver.length);
  }
}
